import { ArrowSquareOut, MapPin, LinkedinLogo } from "@phosphor-icons/react";
import ExternalProfiles from "@/components/scholar/ExternalProfiles";

// Academic classroom / lecture banner (Unsplash; royalty-free)
const HEADER_BG =
  "https://images.unsplash.com/photo-1571260899304-425eee4c7efc?auto=format&fit=crop&w=1920&q=80";

const FALLBACK_AVATAR =
  "https://images.unsplash.com/photo-1758685734463-98672a67dd1c?crop=entropy&cs=srgb&fm=jpg&w=400&q=85";

const INTEREST_BADGE = ["badge-terracotta", "badge-saffron", "badge-teal", "badge-indigo"];

function formatSynced(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export const ProfileHeader = ({
  profile,
  lastSyncedAt,
  externalProfiles = [],
  customPhotoUrl,
  displayName,
}) => {
  const photo = customPhotoUrl || profile.photo || FALLBACK_AVATAR;
  const name = displayName || profile.name || "Unknown Author";
  const linkedin = externalProfiles.find((p) => p.color === "linkedin");
  const otherProfiles = externalProfiles.filter((p) => p.color !== "linkedin");

  return (
    <div data-testid="profile-header" className="relative">
      {/* Lecture hall banner */}
      <div
        className="relative h-60 sm:h-72 lg:h-80 w-full border border-paper-edge overflow-hidden"
        data-testid="profile-banner"
      >
        <img
          src={HEADER_BG}
          alt="Lecture hall"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Soft brightness wash to keep image visible but unify the palette */}
        <div className="absolute inset-0 bg-ink/10" />
        {/* Color accent ribbon at the top */}
        <div className="absolute top-0 left-0 right-0 h-2 z-20 flex">
          <span className="flex-1 bg-terracotta" />
          <span className="flex-1 bg-saffron" />
          <span className="flex-1 bg-teal" />
          <span className="flex-1 bg-indigo" />
          <span className="flex-1 bg-linkedin" />
        </div>
        {/* Color accent ribbon at the bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-1.5 z-20 flex">
          <span className="flex-1 bg-linkedin" />
          <span className="flex-1 bg-indigo" />
          <span className="flex-1 bg-teal" />
          <span className="flex-1 bg-saffron" />
          <span className="flex-1 bg-terracotta" />
        </div>
        {/* Subtle color tints at corners + bottom legibility gradient */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(60% 70% at 0% 0%, rgba(232,163,61,0.45) 0%, rgba(232,163,61,0) 60%), radial-gradient(60% 70% at 100% 0%, rgba(46,58,135,0.40) 0%, rgba(46,58,135,0) 60%), linear-gradient(180deg, rgba(26,26,26,0) 45%, rgba(26,26,26,0.65) 100%)",
          }}
        />
        <div className="absolute inset-0 grain pointer-events-none opacity-50" />

        <div className="relative z-10 h-full flex flex-col justify-between p-5 sm:p-7 lg:p-9">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <span className="inline-flex items-center gap-2 overline bg-white px-3 py-1.5 border border-ink/10 text-ink shadow-sm">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-terracotta animate-pulse" />
              Researcher Dossier
            </span>
            <div className="flex flex-wrap items-center gap-2 max-w-[70%] justify-end">
              {linkedin && (
                <a
                  href={linkedin.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="linkedin-cta"
                  data-testid="header-linkedin-cta"
                >
                  <LinkedinLogo size={16} weight="fill" />
                  <span>LinkedIn</span>
                </a>
              )}
              <span className="overline bg-white/95 px-3 py-1.5 backdrop-blur-sm border border-ink/10 text-ink">
                Synced · {formatSynced(lastSyncedAt)}
              </span>
            </div>
          </div>
          <div className="text-white max-w-3xl drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">
            <div className="font-hand text-3xl sm:text-4xl lg:text-5xl leading-snug">
              &ldquo;Teaching <span className="text-lime">&amp;</span> research, side by side — a living ledger.&rdquo;
            </div>
            <div className="overline mt-3 text-white/95">— A scholar&apos;s editorial record</div>
          </div>
        </div>
      </div>

      {/* Identity row — photo fully visible, sits below banner */}
      <div className="px-6 sm:px-10 mt-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-6">
        <div className="flex flex-col sm:flex-row sm:items-start gap-5 sm:gap-7">
          <div className="relative shrink-0 self-start">
            <span className="absolute -top-3 -left-3 w-10 h-10 bg-coral rotate-12 -z-10" />
            <span className="absolute -bottom-3 -right-3 w-12 h-12 bg-mint -rotate-6 -z-10" />
            <div className="absolute -inset-1.5 bg-gradient-to-br from-coral via-saffron to-mint opacity-95 -z-10" />
            <img
              src={photo}
              alt={profile.name}
              data-testid="profile-photo"
              referrerPolicy="no-referrer"
              className="block w-32 h-32 sm:w-40 sm:h-40 object-cover border-4 border-paper bg-paper-alt shadow-lg"
              onError={(e) => {
                if (e.currentTarget.src !== FALLBACK_AVATAR) {
                  e.currentTarget.src = FALLBACK_AVATAR;
                }
              }}
            />
          </div>
          <div className="pt-1">
            {(() => {
              const parts = name.trim().split(" ");
              const last = parts.pop();
              const rest = parts.join(" ");
              return (
                <h1
                  className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]"
                  data-testid="profile-name"
                >
                  {rest && <span>{rest} </span>}
                  <span className="hl-hotpink">{last}</span>
                </h1>
              );
            })()}
            <p className="mt-3 text-ink-mute flex items-start gap-2 max-w-xl">
              <MapPin size={16} weight="duotone" className="mt-1 shrink-0 text-terracotta" />
              <span data-testid="profile-affiliation">
                {profile.affiliation || "Affiliation unavailable"}
              </span>
            </p>
            {profile.interests?.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2" data-testid="profile-interests">
                {profile.interests.map((i, idx) => (
                  <a
                    key={i.label}
                    href={i.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`pill-color ${INTEREST_BADGE[idx % INTEREST_BADGE.length]} hover:-translate-y-[1px]`}
                    data-testid={`interest-${i.label.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    {i.label}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
        {profile.homepage && (
          <a
            href={profile.homepage}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary text-xs self-start sm:self-end no-print"
            data-testid="homepage-link"
          >
            Homepage <ArrowSquareOut size={14} weight="bold" />
          </a>
        )}
      </div>

      {otherProfiles.length > 0 && (
        <div className="px-6 sm:px-10 mt-5">
          <ExternalProfiles profiles={otherProfiles} compact />
        </div>
      )}
    </div>
  );
};

export default ProfileHeader;
